import { useState, useEffect } from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, auth } from "../config/firebase";
import { useBudget } from "../context/BudgetContext";

const manageMemberFn = httpsCallable(getFunctions(getApp(), "us-central1"), "manageMember");

const ROLES = {
  admin:  { label: "admin",  color: "var(--green)" },
  member: { label: "member", color: "var(--blue)" },
  viewer: { label: "viewer", color: "#555" },
};

function randomCode()  { return String(100000 + (crypto.getRandomValues(new Uint32Array(1))[0] % 900000)); }
function randomToken() {
  return Array.from(crypto.getRandomValues(new Uint8Array(24)), b => b.toString(16).padStart(2, "0")).join("");
}

function Avatar({ member, size = 36 }) {
  const role = ROLES[member.role] ?? ROLES.member;
  const initials = (member.name || member.email || "?")
    .split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  if (member.photoURL) {
    return (
      <img
        src={member.photoURL}
        alt={member.name || member.email}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: `1px solid ${role.color}22`, flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "#0a0a0a", border: `1px solid ${role.color}22`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <span className="mono" style={{ fontSize: size * 0.28, color: role.color }}>{initials}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Team() {
  const { workspaceId, userRole } = useBudget();
  const [currentUser, setCurrentUser] = useState(null);
  const [workspace, setWorkspace]     = useState(null);
  const [copied, setCopied]           = useState("");
  const [roleChanging, setRoleChanging] = useState(null);

  useEffect(() => { const u = onAuthStateChanged(auth, u => setCurrentUser(u)); return u; }, []);
  useEffect(() => {
    if (!workspaceId) return;
    return onSnapshot(doc(db, "workspaces", workspaceId), snap => {
      if (snap.exists()) setWorkspace({ id: snap.id, ...snap.data() });
    });
  }, [workspaceId]);

  const isAdmin = userRole === "admin";
  const wsRef = () => workspaceId ? doc(db, "workspaces", workspaceId) : null;

  // Ensure invite tokens exist
  useEffect(() => {
    if (!workspace || !isAdmin || !workspaceId) return;
    const updates = {};
    if (!workspace.inviteToken) updates.inviteToken = randomToken();
    if (!workspace.inviteCode)  updates.inviteCode  = randomCode();
    if (Object.keys(updates).length > 0) updateDoc(doc(db, "workspaces", workspaceId), updates);
  }, [workspace?.inviteToken, workspace?.inviteCode, workspaceId, isAdmin]);

  const activeMembers  = (workspace?.members ?? []).filter(m => m.status === "active");
  const pendingMembers = (workspace?.joinRequests ?? []);
  const inviteLink = workspace?.inviteToken
    ? `${window.location.origin}/join?token=${workspace.inviteToken}`
    : "";

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  };

  const regenerateLink = () => { if (!wsRef()) return; updateDoc(wsRef(), { inviteToken: randomToken() }); };
  const regenerateCode = () => { if (!wsRef()) return; updateDoc(wsRef(), { inviteCode:  randomCode()  }); };

  // Legacy: approve pending requests from old flow
  const approveRequest = async (req) => {
    if (!wsRef()) return;
    const updated = (workspace.joinRequests ?? []).filter(r => r.uid !== req.uid);
    await updateDoc(wsRef(), {
      joinRequests: updated,
      members: [...(workspace.members ?? []), { ...req, status: "active", approvedAt: new Date().toISOString() }],
    });
    await updateDoc(doc(db, "users", req.uid), { workspaceId, role: req.role ?? "member" });
  };

  const denyRequest = async (req) => {
    if (!wsRef()) return;
    await updateDoc(wsRef(), { joinRequests: (workspace.joinRequests ?? []).filter(r => r.uid !== req.uid) });
  };

  const handleRoleChange = async (memberUid, newRole) => {
    if (!workspaceId) return;
    setRoleChanging(memberUid);
    try {
      await manageMemberFn({ workspaceId, targetUid: memberUid, action: "setRole", role: newRole });
    } catch (err) {
      console.error("[greenline] role change failed:", err);
    }
    setRoleChanging(null);
  };

  const removeMember = async (memberUid) => {
    if (!workspaceId) return;
    try {
      await manageMemberFn({ workspaceId, targetUid: memberUid, action: "remove" });
    } catch (err) {
      console.error("[greenline] remove member failed:", err);
    }
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Team</div>
          <div className="page-subtitle">
            {activeMembers.length} member{activeMembers.length !== 1 ? "s" : ""}
            {pendingMembers.length > 0 ? ` · ${pendingMembers.length} pending` : ""}
            {workspace?.name ? ` · ${workspace.name}` : ""}
          </div>
        </div>
      </div>

      {/* Members grid */}
      <div className="card" style={{ marginBottom: 8, padding: 0 }}>
        <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span className="label" style={{ marginBottom: 0 }}>MEMBERS</span>
          <span className="mono" style={{ fontSize: 9, color: "#333" }}>{activeMembers.length}</span>
        </div>

        {activeMembers.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <span className="mono" style={{ fontSize: 11, color: "#555" }}>no members yet — share the invite link below</span>
          </div>
        ) : (
          <div>
            {activeMembers.map((member, i) => {
              const role = ROLES[member.role] ?? ROLES.member;
              const isOwner = member.uid === workspace?.ownerId;
              const isMe = member.uid === currentUser?.uid;
              const joinedDate = formatDate(member.joinedAt || member.approvedAt);

              return (
                <div
                  key={member.uid}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 16px",
                    borderBottom: i < activeMembers.length - 1 ? "1px solid #0a0a0a" : "none",
                  }}
                >
                  <Avatar member={member} size={36} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <span className="mono" style={{ fontSize: 12, color: "#ccc", fontWeight: 500 }}>
                        {member.name || member.email}
                      </span>
                      {isMe && (
                        <span className="mono" style={{ fontSize: 9, color: "#444", background: "#111", border: "1px solid #1a1a1a", borderRadius: 4, padding: "1px 5px" }}>you</span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="mono" style={{ fontSize: 10, color: "#444", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {member.email}
                      </span>
                      {joinedDate && (
                        <span className="mono" style={{ fontSize: 9, color: "#2a2a2a", flexShrink: 0 }}>joined {joinedDate}</span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {isAdmin && !isOwner && !isMe ? (
                      <select
                        value={member.role}
                        disabled={roleChanging === member.uid}
                        onChange={e => handleRoleChange(member.uid, e.target.value)}
                        style={{ fontSize: 10, color: role.color, padding: "3px 6px", background: "#050505", border: "1px solid #1a1a1a", borderRadius: 4 }}
                      >
                        {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    ) : (
                      <span className="badge badge-gray" style={{ color: role.color, borderColor: `${role.color}22` }}>
                        {isOwner ? "owner" : role.label}
                      </span>
                    )}
                    {isAdmin && !isOwner && !isMe && (
                      <button
                        className="btn btn-danger"
                        onClick={() => removeMember(member.uid)}
                        style={{ fontSize: 9, padding: "3px 7px" }}
                      >
                        remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legacy pending requests */}
      {pendingMembers.length > 0 && (
        <div className="card hover-amber" style={{ marginBottom: 8, borderColor: "var(--amber)22" }}>
          <div className="label" style={{ marginBottom: 10 }}>
            JOIN_REQUESTS <span className="badge badge-amber" style={{ marginLeft: 6 }}>{pendingMembers.length}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {pendingMembers.map(req => (
              <div key={req.uid} style={{ display: "flex", alignItems: "center", gap: 10, background: "#050505", border: "1px solid #111", borderRadius: 6, padding: "8px 10px" }}>
                <Avatar member={req} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mono" style={{ fontSize: 11, color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{req.name || req.email}</div>
                  <div className="mono" style={{ fontSize: 9, color: "#444" }}>{req.email}</div>
                </div>
                <div style={{ display: "flex", gap: 5 }}>
                  <button className="btn btn-green" onClick={() => approveRequest(req)} style={{ fontSize: 10, padding: "4px 10px" }}>approve</button>
                  <button className="btn btn-danger" onClick={() => denyRequest(req)} style={{ fontSize: 10, padding: "4px 10px" }}>deny</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && (
        <>
          {/* Invite link */}
          <div className="card hover-green" style={{ marginBottom: 8 }}>
            <div className="label" style={{ marginBottom: 4 }}>INVITE_LINK</div>
            <div className="mono" style={{ fontSize: 9, color: "#444", marginBottom: 8 }}>share this link — anyone who opens it joins instantly</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <div className="mono" style={{ flex: 1, fontSize: 10, color: "#666", background: "#050505", border: "1px solid #111", borderRadius: 6, padding: "7px 10px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {inviteLink || "generating..."}
              </div>
              <button className={`btn${copied === "link" ? " btn-green" : ""}`} onClick={() => copy(inviteLink, "link")} style={{ fontSize: 10 }}>
                {copied === "link" ? "✓ copied" : "copy"}
              </button>
            </div>
            <button className="btn btn-danger" onClick={regenerateLink} style={{ fontSize: 9 }}>↻ regenerate link</button>
          </div>

          {/* Invite code */}
          <div className="card hover-blue" style={{ marginBottom: 8 }}>
            <div className="label" style={{ marginBottom: 4 }}>INVITE_CODE</div>
            <div className="mono" style={{ fontSize: 9, color: "#444", marginBottom: 8 }}>or share this 6-digit code at greenline.app/join</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
              <div className="mono" style={{ fontSize: 22, fontWeight: 500, color: "#888", letterSpacing: "0.25em", background: "#050505", border: "1px solid #111", borderRadius: 6, padding: "7px 14px" }}>
                {workspace?.inviteCode ?? "------"}
              </div>
              <button className={`btn${copied === "code" ? " btn-green" : ""}`} onClick={() => copy(workspace?.inviteCode ?? "", "code")} style={{ fontSize: 10 }}>
                {copied === "code" ? "✓ copied" : "copy"}
              </button>
            </div>
            <button className="btn btn-danger" onClick={regenerateCode} style={{ fontSize: 9 }}>↻ regenerate code</button>
          </div>
        </>
      )}
    </div>
  );
}
